<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import AiAPI from 'custom/api/ai';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';

// Assistant › Setup: who it is, how it writes (length, neutral language, the team's tone learned from its replies),
// when it hands off, where it works and what it may do
const props = defineProps({
  assistant: { type: Object, required: true },
});
const emit = defineEmits(['saved']);

const { t, locale } = useLocoI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');

const ABILITIES = [
  'handoff',
  'resolve',
  'add_label',
  'search_knowledge',
  'pixta',
];
const REPLY_LENGTHS = ['short', 'medium', 'detailed'];
const TONE_POLL_MS = 4000;
const TONE_WAIT_MS = 120000;
const TEMPERATURES = [
  { value: 0.1, key: 'PRECISE' },
  { value: 0.3, key: 'BALANCED' },
  { value: 0.7, key: 'CREATIVE' },
];

const pick = assistant => ({
  name: assistant.name,
  description: assistant.description || '',
  instructions: assistant.instructions || '',
  guardrails: assistant.guardrails || '',
  handoff_instructions: assistant.handoff_instructions || '',
  inbox_ids: [...assistant.inbox_ids],
  // A copy to edit (structuredClone can't copy the reactive proxy)
  settings: JSON.parse(JSON.stringify(assistant.settings)),
});

const form = reactive(pick(props.assistant));
const isSaving = ref(false);
const isLearningTone = ref(false);
const toneLearnedAt = ref(props.assistant.settings.style_learned_at);
let tonePoll = null;

const toneLearnedLabel = computed(() =>
  toneLearnedAt.value
    ? t('LOCO.AI.SETUP.TONE_LEARNED', {
        date: new Date(toneLearnedAt.value).toLocaleString(
          intlLocale(locale.value)
        ),
      })
    : ''
);

const temperature = computed({
  get: () =>
    TEMPERATURES.reduce((closest, option) =>
      Math.abs(option.value - form.settings.temperature) <
      Math.abs(closest.value - form.settings.temperature)
        ? option
        : closest
    ).value,
  set: value => {
    form.settings.temperature = value;
  },
});

const toggleInbox = id => {
  form.inbox_ids = form.inbox_ids.includes(id)
    ? form.inbox_ids.filter(inboxId => inboxId !== id)
    : [...form.inbox_ids, id];
};

const toggleAbility = name => {
  const tools = form.settings.tools || [];
  form.settings.tools = tools.includes(name)
    ? tools.filter(tool => tool !== name)
    : [...tools, name];
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await AiAPI.updateAssistant(props.assistant.id, form);
    Object.assign(form, pick(data));
    emit('saved', data);
    useAlert(t('LOCO.AI.SETUP.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSaving.value = false;
  }
};

// The tone is learned in the background; only the tone fields are refreshed, so other unsaved edits stay
const waitForTone = (previous, deadline) => {
  tonePoll = setTimeout(async () => {
    try {
      const { data } = await AiAPI.assistant(props.assistant.id);
      if (data.settings.style_learned_at !== previous) {
        form.settings.style_guide = data.settings.style_guide;
        toneLearnedAt.value = data.settings.style_learned_at;
        isLearningTone.value = false;
        return;
      }
    } catch {
      // A failed check is retried until the deadline
    }
    if (Date.now() > deadline) {
      isLearningTone.value = false;
      useAlert(t('LOCO.AI.SETUP.TONE_TIMEOUT'));
      return;
    }
    waitForTone(previous, deadline);
  }, TONE_POLL_MS);
};

const learnTone = async () => {
  isLearningTone.value = true;
  try {
    await AiAPI.learnStyle(props.assistant.id);
    waitForTone(toneLearnedAt.value, Date.now() + TONE_WAIT_MS);
  } catch (error) {
    isLearningTone.value = false;
    useAlert(error.response?.data?.error || error.message);
  }
};

onMounted(() => {
  if (!teams.value.length) store.dispatch('teams/get');
});

onBeforeUnmount(() => clearTimeout(tonePoll));
</script>

<template>
  <form class="flex flex-col gap-8 pb-8" @submit.prevent="save">
    <section class="grid gap-4 md:grid-cols-2">
      <label class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.SETUP.NAME') }}
        </span>
        <input
          v-model="form.name"
          type="text"
          maxlength="80"
          required
          class="!mb-0"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('LOCO.AI.SETUP.NAME_HELP') }}
        </span>
      </label>
      <label class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.SETUP.DESCRIPTION') }}
        </span>
        <input
          v-model="form.description"
          type="text"
          class="!mb-0"
          :placeholder="t('LOCO.AI.SETUP.DESCRIPTION_PLACEHOLDER')"
        />
      </label>
    </section>

    <section class="flex flex-col gap-4">
      <label class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.SETUP.INSTRUCTIONS') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('LOCO.AI.SETUP.INSTRUCTIONS_HELP') }}
        </span>
        <textarea
          v-model="form.instructions"
          rows="8"
          class="!mb-0 !h-auto resize-y"
          :placeholder="t('LOCO.AI.SETUP.INSTRUCTIONS_PLACEHOLDER')"
        />
      </label>
      <label class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.SETUP.GUARDRAILS') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('LOCO.AI.SETUP.GUARDRAILS_HELP') }}
        </span>
        <textarea
          v-model="form.guardrails"
          rows="3"
          class="!mb-0 !h-auto resize-y"
          :placeholder="t('LOCO.AI.SETUP.GUARDRAILS_PLACEHOLDER')"
        />
      </label>
    </section>

    <section class="flex flex-col gap-4">
      <h2 class="text-heading-3 text-n-slate-12">
        {{ t('LOCO.AI.SETUP.WRITING') }}
      </h2>
      <div class="grid gap-4 md:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.REPLY_LENGTH') }}
          </span>
          <select v-model="form.settings.reply_length" class="!mb-0">
            <option
              v-for="length in REPLY_LENGTHS"
              :key="length"
              :value="length"
            >
              {{ t(`LOCO.AI.SETUP.REPLY_LENGTHS.${length}`) }}
            </option>
          </select>
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.SETUP.REPLY_LENGTH_HELP') }}
          </span>
        </label>
        <label class="flex items-start justify-between gap-4 text-sm">
          <span class="flex flex-col gap-0.5">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.AI.SETUP.NEUTRAL_LANGUAGE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('LOCO.AI.SETUP.NEUTRAL_LANGUAGE_HELP') }}
            </span>
          </span>
          <Switch v-model="form.settings.neutral_language" />
        </label>
      </div>
      <div class="flex flex-col gap-1 text-sm">
        <div class="flex flex-wrap items-end justify-between gap-2">
          <span class="flex flex-col gap-1">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.AI.SETUP.TEAM_TONE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('LOCO.AI.SETUP.TEAM_TONE_HELP') }}
            </span>
          </span>
          <Button
            type="button"
            sm
            faded
            slate
            icon="i-lucide-sparkles"
            :label="t('LOCO.AI.SETUP.LEARN_TONE')"
            :is-loading="isLearningTone"
            @click="learnTone"
          />
        </div>
        <textarea
          v-model="form.settings.style_guide"
          rows="12"
          maxlength="4000"
          class="!mb-0 !h-auto resize-y"
          :disabled="isLearningTone"
          :placeholder="t('LOCO.AI.SETUP.TEAM_TONE_PLACEHOLDER')"
        />
        <span v-if="isLearningTone" class="text-xs text-n-slate-11">
          {{ t('LOCO.AI.SETUP.LEARNING_TONE') }}
        </span>
        <span v-else-if="toneLearnedLabel" class="text-xs text-n-slate-11">
          {{ toneLearnedLabel }}
        </span>
      </div>
    </section>

    <section class="flex flex-col gap-4">
      <h2 class="text-heading-3 text-n-slate-12">
        {{ t('LOCO.AI.SETUP.HANDOFF') }}
      </h2>
      <label class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.SETUP.HANDOFF_RULES') }}
        </span>
        <textarea
          v-model="form.handoff_instructions"
          rows="3"
          class="!mb-0 !h-auto resize-y"
          :placeholder="t('LOCO.AI.SETUP.HANDOFF_RULES_PLACEHOLDER')"
        />
      </label>
      <div class="grid gap-4 md:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.HANDOFF_TEAM') }}
          </span>
          <select v-model="form.settings.handoff_team_id" class="!mb-0">
            <option :value="null">{{ t('LOCO.AI.SETUP.ANY_TEAM') }}</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.HANDOFF_MESSAGE') }}
          </span>
          <input
            v-model="form.settings.handoff_message"
            type="text"
            class="!mb-0"
            :placeholder="t('LOCO.AI.SETUP.HANDOFF_MESSAGE_PLACEHOLDER')"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.SETUP.HANDOFF_MESSAGE_HELP') }}
          </span>
        </label>
      </div>
    </section>

    <section class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h2 class="text-heading-3 text-n-slate-12">
          {{ t('LOCO.AI.SETUP.INBOXES') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('LOCO.AI.SETUP.INBOXES_HELP') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="inbox in inboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center gap-2 h-8 px-3 rounded-full text-sm outline outline-1 -outline-offset-1"
          :class="
            form.inbox_ids.includes(inbox.id)
              ? 'bg-n-brand/10 outline-n-brand text-n-blue-11'
              : 'outline-n-weak text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="toggleInbox(inbox.id)"
        >
          <span
            :class="
              form.inbox_ids.includes(inbox.id)
                ? 'i-lucide-check-circle-2'
                : 'i-lucide-circle'
            "
            class="size-4"
          />
          {{ inbox.name }}
        </button>
      </div>
    </section>

    <section class="flex flex-col gap-4">
      <h2 class="text-heading-3 text-n-slate-12">
        {{ t('LOCO.AI.SETUP.BEHAVIOR') }}
      </h2>
      <div class="grid gap-4 md:grid-cols-3">
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.STYLE') }}
          </span>
          <select v-model="temperature" class="!mb-0">
            <option
              v-for="option in TEMPERATURES"
              :key="option.value"
              :value="option.value"
            >
              {{ t(`LOCO.AI.SETUP.STYLES.${option.key}`) }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.REPLY_DELAY') }}
          </span>
          <input
            v-model.number="form.settings.reply_delay_seconds"
            type="number"
            min="0"
            max="60"
            class="!mb-0"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.SETUP.REPLY_DELAY_HELP') }}
          </span>
        </label>
        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.MAX_REPLIES') }}
          </span>
          <input
            v-model.number="form.settings.max_replies"
            type="number"
            min="0"
            max="100"
            class="!mb-0"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.SETUP.MAX_REPLIES_HELP') }}
          </span>
        </label>
      </div>
      <label class="flex items-start justify-between gap-4 text-sm">
        <span class="flex flex-col gap-0.5">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.OUTSIDE_HOURS') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.SETUP.OUTSIDE_HOURS_HELP') }}
          </span>
        </span>
        <Switch v-model="form.settings.only_outside_business_hours" />
      </label>
      <label class="flex items-start justify-between gap-4 text-sm">
        <span class="flex flex-col gap-0.5">
          <span class="font-medium text-n-slate-12">
            {{ t('LOCO.AI.SETUP.CITE_SOURCES') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('LOCO.AI.SETUP.CITE_SOURCES_HELP') }}
          </span>
        </span>
        <Switch v-model="form.settings.cite_sources" />
      </label>
    </section>

    <section class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h2 class="text-heading-3 text-n-slate-12">
          {{ t('LOCO.AI.SETUP.ABILITIES') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('LOCO.AI.SETUP.ABILITIES_HELP') }}
        </p>
      </div>
      <label
        v-for="ability in ABILITIES"
        :key="ability"
        class="flex items-start gap-3 text-sm"
      >
        <input
          type="checkbox"
          class="mt-0.5"
          :checked="(form.settings.tools || []).includes(ability)"
          @change="toggleAbility(ability)"
        />
        <span class="flex flex-col gap-0.5">
          <span class="font-medium text-n-slate-12">
            {{ t(`LOCO.AI.SETUP.ABILITY.${ability.toUpperCase()}`) }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t(`LOCO.AI.SETUP.ABILITY.${ability.toUpperCase()}_HELP`) }}
          </span>
        </span>
      </label>
    </section>

    <div
      class="sticky bottom-0 -mx-6 px-6 py-3 bg-n-surface-1/95 backdrop-blur border-t border-n-weak"
    >
      <Button
        type="submit"
        :label="t('LOCO.AI.SETUP.SAVE')"
        :is-loading="isSaving"
      />
    </div>
  </form>
</template>
